import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ChainRepository, Fill, Trade } from 'common';
import { Rebalancing } from '../../../model';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { heroArrowTurnDownRightMini } from '@ng-icons/heroicons/mini';
import { Chart, registerables } from 'chart.js';
import { Flow, SankeyController } from 'chartjs-chart-sankey';
import { RebalancingRepository } from '../../../repository';

@Component({
  selector: 'common-rebalancing-trades-sankey',
  standalone: true,
  imports: [CommonModule, TranslateModule, NgIconComponent],
  providers: [
    provideIcons({
      heroArrowTurnDownRightMini,
    }),
  ],
  templateUrl: './trade-sankey.component.html',
  styleUrls: ['./trade-sankey.component.scss'],
})
export class TradeSankeyComponent implements OnInit {
  @Input()
  public trades: Trade[];

  @ViewChild('sankeyChart')
  private chartRef: ElementRef;

  private fills: Fill[] = [];
  private data: any[] = [];
  private colors: { [key: string]: string } = {};
  private getColor = (key: string) => this.colors[key];
  private chart: Chart;

  public showChart = false;

  constructor(public chainRepository: ChainRepository) {}

  public ngOnInit(): void {
    this.prepareSankeyChart();
  }

  private prepareSankeyChart(): void {
    let totalValue = 0;
    this.trades?.forEach(trade => {
      totalValue += trade.fromValueInUsd;
    });

    this.trades?.forEach(trade => {
      // TODO: maybe also add subfills here
      this.fills.push(trade.fill);

      const proportion = trade.fromValueInUsd / totalValue;
      this.data.push({
        from: trade.fill.from.symbol,
        to: trade.fill.to.symbol,
        flow: trade.fill.proportionInBps * proportion,
      });

      this.colors[trade.fill.liquiditySource.readableName] =
        '#' +
        (0x1000000 + Math.random() * 0xffffff).toString(16).substring(1, 7);
      this.colors[trade.fill.from.symbol] =
        trade.fill.from.sliderColor || 'red';
      this.colors[trade.fill.to.symbol] = trade.fill.to.sliderColor || 'green';
    });

    Chart.register(SankeyController, Flow, ...registerables);
  }

  public toggleChart(): void {
    this.showChart = !this.showChart;
    if (this.showChart) {
      setTimeout(() => {
        this.drawSankeyChart();
      }, 1);
    }
  }

  private drawSankeyChart(): void {
    this.chart = new Chart(this.chartRef.nativeElement, {
      type: 'sankey',
      data: {
        datasets: [
          {
            label: 'Token flow',
            data: this.data,

            colorFrom: c => this.getColor(c.dataset.data[c.dataIndex].from),
            colorTo: c => this.getColor(c.dataset.data[c.dataIndex].to),
            hoverColorFrom: c =>
              this.getColor(c.dataset.data[c.dataIndex].from),
            hoverColorTo: c => this.getColor(c.dataset.data[c.dataIndex].to),
            colorMode: 'gradient', // or 'from' or 'to'

            size: 'max', // or 'min' if flow overlap is preferred
          },
        ],
      },
      options: {
        plugins: {
          tooltip: {
            callbacks: {
              label: context => {
                const index = context.dataIndex;
                const percentage =
                  Math.round((context.raw as { flow: number }).flow) / 100;
                const liqSourceName =
                  this.fills[index].liquiditySource.readableName;
                const fromSymbol = this.fills[index].from.symbol;
                const toSymbol = this.fills[index].to.symbol;
                return `${percentage}% ${liqSourceName} (${fromSymbol}/${toSymbol})`;
              },
            },
          },
        },
      },
    });
  }
}
